import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "welcome-to-police-narratives-ai",
  courseId: "getting-started",
  order: 1,
  title: "Welcome",
  subtitle: "A tour of the workspace",
  summary:
    "You record or upload audio, the AI drafts the narrative in your style, and you review it in the editor. This lesson shows where cases live, where new reports start, and how Settings differs from Report Preferences.",
  learn: [
    "Find your way around the sidebar and the cases list",
    "Start a new case four different ways",
    "Tell the Settings page apart from Report Preferences",
  ],
  minutes: 3,
  sceneId: "welcome-to-police-narratives-ai",
  tourId: "welcome-to-police-narratives-ai",
  releasedAt: "2025-06-01T00:00:00.000Z",
  body: [
    {
      type: "paragraph",
      text: "Police Narratives AI turns what you say into a finished report. You record or upload audio, the AI transcribes it and drafts the narrative in your style, and you review it in an editor built for police writing. Everything starts from the sidebar.",
    },
    { type: "heading", text: "The sidebar" },
    {
      type: "bullets",
      items: [
        "**New Case** at the top opens a menu: **Record Audio**, **Upload Files**, **Blank Report**, and **Checklist** (only if your department has checklist templates).",
        "The sliders icon beside it opens **Report Preferences**, the page that controls how your drafts are written.",
        "**Cases** is your list of reports. **Trash** holds deleted cases, and you can also drag a case onto it.",
        "**Files**, **Forms**, and **Redactions** are the other work areas. **Investigations**, **Policies**, and **Feature Requests** appear only when your account has access to them.",
        "Below the items are your categories, starting with **Favorites**. The footer shows your name and badge number, a **Toggle dark mode** switch, and a gear that opens Settings.",
      ],
    },
    { type: "heading", text: "Try it" },
    {
      type: "steps",
      items: [
        "Click **Cases** in the sidebar and use the search box (\"Search cases...\") to find a report by title or content.",
        "Click **New Case** and look at the four options. Close the menu without picking one.",
        "Click the sliders icon next to **New Case** to open **Report Preferences**.",
        "Click the gear in the sidebar footer to open **Settings**.",
        "Open any case. The narrative is on the left, and the right panel has **Suggestions**, **Snippets**, **Statutes**, and **Redactions** tabs.",
      ],
    },
    {
      type: "paragraph",
      text: "Report Preferences covers perspective, date and time format, rank, badge, and templates. Settings covers your name, email, password, plan, appearance, and security.",
    },
    {
      type: "callout",
      tone: "tip",
      title: "Settings vs preferences",
      text: "Settings is about your account. Report Preferences is about your writing. If a draft comes out in the wrong perspective or time format, the fix is in Report Preferences, not Settings.",
    },
    {
      type: "callout",
      tone: "note",
      text: "If you belong to an agency, your email, department, and billing rows on the Settings page are read-only and marked \"Managed by your department\". Your admin changes those.",
    },
    {
      type: "paragraph",
      text: "Next, set up your report preferences so the first draft comes out in your voice.",
    },
  ],
  related: ["set-up-your-report-preferences", "first-narrative-report"],
};

export default lesson;
